<template>
    <canvas ref="canvas"></canvas>
</template>

<script>
import Chart from 'chart.js/auto'

export default {
  name: 'RateDistributionChart',
  props: {
    tasks: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      chart: null
    }
  },
  mounted() {
    this.createChart()
  },
  watch: {
    tasks: {
      handler() {
        this.updateChart()
      },
      deep: true
    }
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.destroy()
    }
  },
  methods: {
    createChart() {
      const ctx = this.$refs.canvas.getContext('2d')
      
      this.chart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: this.getBinLabels(),
          datasets: [{
            label: 'Number of Tasks',
            data: this.getDistributionData(),
            backgroundColor: 'rgba(76, 175, 80, 0.6)',
            borderColor: 'rgba(76, 175, 80, 1)',
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: 'Rate Distribution (kg/hr)'
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  return `${context.parsed.y} tasks`
                }
              }
            }
          },
          scales: {
            x: {
              title: {
                display: true,
                text: 'Rate Range (kg/hr)'
              }
            },
            y: {
              title: {
                display: true,
                text: 'Number of Tasks'
              },
              beginAtZero: true,
              ticks: {
                stepSize: 1
              }
            }
          }
        }
      })
    },
    
    updateChart() {
      if (this.chart) {
        this.chart.data.labels = this.getBinLabels()
        this.chart.data.datasets[0].data = this.getDistributionData()
        this.chart.update()
      }
    },
    
    getDistributionData() {
      const rates = this.tasks.map(task => this.calculateRate(task.description, task.elapsed))
      const bins = this.createBins(rates)
      return bins.map(bin => bin.count)
    },
    
    getBinLabels() {
      const rates = this.tasks.map(task => this.calculateRate(task.description, task.elapsed))
      const bins = this.createBins(rates)
      return bins.map(bin => `${bin.min.toFixed(1)}-${bin.max.toFixed(1)}`)
    },
    
    createBins(rates) {
      if (rates.length === 0) return []
      
      const validRates = rates.filter(rate => rate > 0)
      if (validRates.length === 0) return []
      
      // Remove outliers using IQR method
      const sortedRates = [...validRates].sort((a, b) => a - b)
      const q1 = sortedRates[Math.floor(sortedRates.length * 0.25)]
      const q3 = sortedRates[Math.floor(sortedRates.length * 0.75)]
      const iqr = q3 - q1
      const lowerBound = q1 - 1.5 * iqr
      const upperBound = q3 + 1.5 * iqr
      
      const clippedRates = validRates.filter(rate => rate >= lowerBound && rate <= upperBound)
      
      if (clippedRates.length === 0) {
        // Fallback to original data if all rates are outliers
        const minRate = Math.min(...validRates)
        const maxRate = Math.max(...validRates)
        return [{
          min: minRate,
          max: maxRate,
          count: validRates.length
        }]
      }
      
      const minRate = Math.min(...clippedRates)
      const maxRate = Math.max(...clippedRates)
      const binCount = Math.min(8, clippedRates.length) // Max 8 bins, or number of tasks if less
      
      if (minRate === maxRate) {
        return [{
          min: minRate,
          max: maxRate,
          count: clippedRates.length
        }]
      }
      
      const binWidth = (maxRate - minRate) / binCount
      const bins = []
      
      for (let i = 0; i < binCount; i++) {
        const binMin = minRate + (i * binWidth)
        const binMax = minRate + ((i + 1) * binWidth)
        
        const count = clippedRates.filter(rate => {
          return i === binCount - 1 ? 
            (rate >= binMin && rate <= binMax) : 
            (rate >= binMin && rate < binMax)
        }).length
        
        bins.push({
          min: binMin,
          max: binMax,
          count: count
        })
      }
      
      return bins
    },
    
    calculateRate(description, elapsed) {
      const totalWeightGrams = this.calculateTotalWeight(description)
      const totalWeightKg = totalWeightGrams / 1000
      
      if (!elapsed || elapsed === 0 || totalWeightKg === 0) {
        return 0
      }
      
      const durationHours = elapsed / (1000 * 60 * 60)
      return totalWeightKg / durationHours
    },
    
    calculateTotalWeight(description) {
      if (!description) return 0
      
      let totalWeight = 0
      const lines = description.split('\n')
      
      for (const line of lines) {
        const trimmedLine = line.trim()
        if (trimmedLine) {
          const weightMatch = trimmedLine.match(/^(\d+(?:\.\d+)?)\s*(g|kg|grams?|kilograms?)/i)
          if (weightMatch) {
            const value = parseFloat(weightMatch[1])
            const unit = weightMatch[2].toLowerCase()
            
            if (unit.startsWith('kg') || unit.startsWith('kilogram')) {
              totalWeight += value * 1000
            } else {
              totalWeight += value
            }
          }
        }
      }
      
      return totalWeight
    }
  }
}
</script>

<style scoped>
</style>